import React, { useState, useEffect, useRef } from 'react';
import { KnockingWaitingRoom } from '../channels/KnockingWaitingRoom';
import type { GuestAdmissionState } from '../channels/useKnockingState';

interface GuestPortalProps {
  inviteToken: string;
  onAdmitted: (guestSession: { sessionId: string; token: string; displayName: string; roomId: string }) => void;
}

export function GuestPortal({ inviteToken, onAdmitted }: GuestPortalProps) {
  const [admissionState, setAdmissionState] = useState<GuestAdmissionState>('preview');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [targetRoomId, setTargetRoomId] = useState('private');
  const [hostOnline, setHostOnline] = useState(true);
  const [knockId, setKnockId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionDataRef = useRef<{ sessionId: string; token: string; roomId: string; name: string } | null>(null);

  useEffect(() => {
    async function checkInvite() {
      try {
        const res = await fetch(`/api/v1/invites/${inviteToken}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (res.status === 410) {
            setAdmissionState((data.status as GuestAdmissionState) || 'invite_revoked');
          } else {
            setError('This invitation link is invalid or has expired.');
          }
        } else {
          const data = await res.json();
          if (data.roomId) setTargetRoomId(data.roomId);
        }
      } catch {
        setError('Unable to reach server. Please check your network connection.');
      }
    }
    checkInvite();
  }, [inviteToken]);

  async function handleKnock() {
    const finalName = displayName.trim() || `Guest-${Math.floor(1000 + Math.random() * 9000)}`;
    setDisplayName(finalName);
    setError(null);

    try {
      const res = await fetch('/api/v1/invites/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: inviteToken,
          displayName: finalName,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit admission request');
      }

      const data = await res.json();
      setKnockId(data.knockId);
      sessionDataRef.current = {
        sessionId: data.guestSessionId,
        token: data.sessionToken,
        roomId: data.roomId || targetRoomId,
        name: finalName,
      };

      setAdmissionState('pending_admission');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error submitting knock');
    }
  }

  useEffect(() => {
    if (!knockId || admissionState !== 'pending_admission') return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/v1/invites/knocks/${knockId}/status`);
        if (!res.ok) return;
        const data = await res.json();

        if (data.status === 'admitted') {
          clearInterval(interval);
          setAdmissionState('admitted');
          if (sessionDataRef.current) {
            onAdmitted({
              sessionId: sessionDataRef.current.sessionId,
              token: sessionDataRef.current.token,
              displayName: sessionDataRef.current.name,
              roomId: sessionDataRef.current.roomId,
            });
          }
        } else if (data.status === 'denied') {
          clearInterval(interval);
          setAdmissionState('denied');
        }
      } catch (err) {
        console.warn('[KnockPoll] Polling warning:', err);
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [knockId, admissionState, onAdmitted]);

  if (error) {
    return (
      <div className="h-screen w-screen bg-neutral-950 flex items-center justify-center p-6 text-center text-white">
        <div className="max-w-md w-full p-8 bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-950/60 border border-red-800 flex items-center justify-center text-red-400 text-xl font-bold mb-4">
            ✕
          </div>
          <h3 className="text-lg font-bold mb-2">Invitation Unavailable</h3>
          <p className="text-xs text-neutral-400 mb-6">{error}</p>
          <button
            onClick={() => (window.location.href = '/')}
            className="w-full py-2.5 text-xs font-semibold bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 rounded-xl text-neutral-300"
          >
            Go to Main Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <KnockingWaitingRoom
      state={admissionState}
      hostOnline={hostOnline}
      knockExpiresAt={null}
      displayName={displayName}
      onDisplayNameChange={setDisplayName}
      email={email}
      onEmailChange={setEmail}
      phone={phone}
      onPhoneChange={setPhone}
      roomId={targetRoomId}
      onKnock={handleKnock}
      onCancel={() => (window.location.href = '/')}
      onReKnock={handleKnock}
    />
  );
}
